import { Button, Spinner, toast } from '@heroui/react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Canvas from '@/features/canvas-editor';
import { useKnowledgeBaseCanvasPersistence } from '@/components/canvas/document/persistence/useKnowledgeBaseCanvasPersistence';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';

/**
 * 大屏画布路由页：`/displays/canvas/:displayId`、`:displayId/play`、`template/:templateId`。
 * 新建在列表页 create 后带 id 进入；从模板打开仍在本页首次保存时 create。
 */
export function DisplayCanvasPage() {
  const { displayId, templateId } = useParams<{ displayId?: string; templateId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();

  const isTemplate = location.pathname.includes('/canvas/template/');
  const isPlay = location.pathname.endsWith('/play');
  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  const loadId = isTemplate ? templateId : displayId;

  useEffect(() => {
    if (!loadId) {
      navigate('/displays', { replace: true });
    }
  }, [loadId, navigate]);

  const displayQuery = trpc.knowledgeBases.get.useQuery(
    { id: loadId! },
    { enabled: Boolean(loadId), staleTime: 0, refetchOnMount: 'always' },
  );

  const persistence = useKnowledgeBaseCanvasPersistence({
    fileFormat: 'display',
    recordId: isTemplate ? undefined : displayId,
    sessionKey: isTemplate ? `template-${templateId}` : undefined,
    initialTitle: isTemplate
      ? displayQuery.data?.title
        ? `${displayQuery.data.title.trim()} 副本`
        : '未命名'
      : displayQuery.data?.title,
    initialContent: displayQuery.data?.content,
    editPathAfterCreate: isTemplate ? (id) => `/displays/canvas/${id}` : undefined,
    enabled: Boolean(displayQuery.data),
  });

  const { flushSave, scheduleContentSave, handleRename, title, initialDocument } = persistence;

  const handleClose = useCallback(() => {
    useCanvasStore.getState().clearTableSession();
    void flushSave();
    void Promise.all([
      utils.knowledgeBases.listMy.invalidate(),
      utils.knowledgeBases.list.invalidate(),
    ]);
    navigate('/displays');
  }, [flushSave, navigate, utils.knowledgeBases.list, utils.knowledgeBases.listMy]);

  const handleManualSave = useCallback(
    async (document: Parameters<typeof scheduleContentSave>[0]) => {
      const ok = await flushSave(document);
      if (ok) toast.success('已保存');
      return ok;
    },
    [flushSave],
  );

  const playProps = {
    autoEnterFullscreen: isPlay,
    playConfig: isPlay
      ? ({ autoPlay: false } as const)
      : null,
  };

  if (!loadId) return null;

  const showLoadError = displayQuery.isError || (!displayQuery.isLoading && !displayQuery.data?.id);
  const showLoading = displayQuery.isLoading;
  const readonly = !currentUserId || displayQuery.data?.ownerId !== currentUserId;

  if (showLoadError) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-2 text-sm text-destructive">
        <span>加载失败</span>
        <Button type="button" variant="secondary" size="sm" onPress={handleClose}>
          返回列表
        </Button>
      </div>
    );
  }

  const editorKey = isTemplate ? `display-template-${templateId}` : `display-${displayId}`;

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {!showLoading ? (
        <Canvas
          mode="slide"
          editorKey={editorKey}
          onRequestClose={handleClose}
          initialDocument={initialDocument}
          fileName={title}
          onDocumentChange={scheduleContentSave}
          onRequestSave={handleManualSave}
          onRenameFile={handleRename}
          readonly={readonly}
          {...playProps}
        />
      ) : (
        <div
          className="flex flex-1 items-center justify-center bg-background"
          aria-busy
          aria-label="加载大屏"
        >
          <Spinner className="size-8" />
        </div>
      )}
    </div>
  );
}
